import { get } from 'lodash-es';
import { useCallback, type ReactNode } from 'react';
import { useTheme, type DefaultTheme } from 'styled-components';
import type { ConnectionStatusEnum } from '../../types';
import type { PropType, StyledComponentAllProps } from '../../utils';
import { connectionStatusText } from '../../utils';
import { BulletText } from '../Bullet';
import { Flex } from '../Core';
import { ICON_SIZES, IconName, type IconProps } from '../Icons';
import { Popover } from '../Popover/Popover';
import type { PopoverProps } from '../Popover/types';
import { usePopoverState } from '../Popover/usePopoverState';
import { BulletSpinner, BulletWithAnimation, StatusWrapper, Text } from './styles';

export { StatusWrapper } from './styles';

const BulletPopover = ({
  color,
  text,
  popoverPlacement,
  isLoading,
  size,
}: Pick<StatusIndicatorProps, 'color' | 'text' | 'popoverPlacement' | 'isLoading' | 'size'>) => {
  const popover = usePopoverState({
    usePortal: true,
    trigger: 'hover',
    isSmall: true,
    placement: popoverPlacement ?? 'right',
    delay: undefined,
    preventOverflow: false,
  });

  return text ? (
    <Popover {...popover}>
      <BulletWithAnimation color={color} isLoading={isLoading} size={size} />
      {text && <BulletText>{text}</BulletText>}
    </Popover>
  ) : (
    <BulletWithAnimation color={color} isLoading={isLoading} size={size} />
  );
};

export interface StatusIndicatorProps extends StyledComponentAllProps<typeof StatusWrapper> {
  color: string;
  text?: ReactNode;
  popoverText?: ReactNode;
  size?: IconProps['size'];
  iconRight?: boolean;
  popoverPlacement?: PropType<PopoverProps, 'placement'>;
  showIcon?: boolean;
  showText?: boolean;
  isLoading?: boolean;
}

export function StatusIndicator({
  color,
  text = '',
  popoverText,
  size = ICON_SIZES.SMALL,
  isLoading = false,
  iconRight = false,
  popoverPlacement = 'right',
  showIcon = true,
  showText = true,
  ...props
}: StatusIndicatorProps) {
  const IndicatorWithSpinner = useCallback(
    () => (
      <Flex position="relative" alignItems="center" justifyContent="center">
        {isLoading && (
          <BulletSpinner data-testid="bullet-spinner" icon={IconName.RefreshRecover} variant={color} size={size} />
        )}
        <BulletPopover
          color={color}
          text={popoverText}
          popoverPlacement={popoverPlacement}
          isLoading={isLoading}
          size={size}
        />
      </Flex>
    ),
    [size, color, popoverText, popoverPlacement, isLoading]
  );
  return (
    <StatusWrapper data-testid="status-indicator" iconRight={iconRight} {...props}>
      {showIcon && !iconRight && <IndicatorWithSpinner />}
      {text !== '' && <Text>{text}</Text>}
      {showIcon && iconRight && <IndicatorWithSpinner />}
    </StatusWrapper>
  );
}

/** Number grading 0-3 where 0 is least available (or unknown), 3 is most available. This is just a UI heuristic... feel free to change in the future */
export enum ConnectionStatusAvailabilityEnum {
  Online = 3,
  Problematic = 2,
  Offline = 1,
  Unknown = 0,
}

const UNKNOWN_CONNECTION_STATUS_METADATA: ConnectionStatusMetadata = { availability: 0, color: 'colorTextDefault' };
interface ConnectionStatusMetadata {
  /** Color to render for each of the statuses */
  color: keyof DefaultTheme;
  /** Put each status in a bucket of availability which lets us compare them to each other on a numeric scale */
  availability: ConnectionStatusAvailabilityEnum;
}

const CONNECTION_STATUS_METADATA: { [key in ConnectionStatusEnum]: ConnectionStatusMetadata } = {
  Online: {
    color: 'colorTextPositive',
    availability: ConnectionStatusAvailabilityEnum.Online,
  },

  Stale: {
    color: 'colorTextWarning',
    availability: ConnectionStatusAvailabilityEnum.Problematic,
  },
  Starting: {
    color: 'colorTextWarning',
    availability: ConnectionStatusAvailabilityEnum.Problematic,
  },
  Stopping: {
    color: 'colorTextWarning',
    availability: ConnectionStatusAvailabilityEnum.Problematic,
  },
  Error: {
    color: 'colorTextWarning', // We want to ensure that we don't use `theme.colorTextNegative` here because we shouldn't show red on the screen
    availability: ConnectionStatusAvailabilityEnum.Problematic,
  },
  RateLimited: {
    color: 'colorTextWarning',
    availability: ConnectionStatusAvailabilityEnum.Problematic,
  },

  Offline: {
    color: 'colorTextMuted',
    availability: ConnectionStatusAvailabilityEnum.Offline,
  },
  Disabled: {
    color: 'colorTextMuted',
    availability: ConnectionStatusAvailabilityEnum.Offline,
  },
  Unavailable: {
    color: 'colorTextMuted',
    availability: ConnectionStatusAvailabilityEnum.Offline,
  },
  NotInSession: {
    color: 'colorTextMuted',
    availability: ConnectionStatusAvailabilityEnum.Offline,
  },
  Keepalive: {
    color: 'colorTextMuted',
    availability: ConnectionStatusAvailabilityEnum.Offline,
  },
};

export function getConnectionStatusMetadata(status: ConnectionStatusEnum | undefined): ConnectionStatusMetadata {
  if (status == null) {
    return UNKNOWN_CONNECTION_STATUS_METADATA;
  }

  return get(CONNECTION_STATUS_METADATA, status) ?? UNKNOWN_CONNECTION_STATUS_METADATA;
}

export const ConnectionStatusIndicator = ({
  status,
  showText,
  ...props
}: Omit<StatusIndicatorProps, 'color'> & {
  status?: ConnectionStatusEnum;
}) => {
  const text = connectionStatusText(status);
  const theme = useTheme();

  const colorVar = getConnectionStatusMetadata(status).color;

  return (
    <StatusIndicator
      color={theme[colorVar]}
      text={showText ? text : undefined}
      iconRight
      data-status={status}
      {...props}
    />
  );
};
